import { ReactNode, useEffect, useRef, useState } from 'react';

import { IconTimesDefault } from '@iwoca/icons';
import { Button } from '@iwoca/orion';
import { Text } from '@iwoca/orion-ui';
import { FormattedMessage } from 'react-intl';

import { SummaryRow } from '../SummaryRow/SummaryRow';

export const MobileLoanSummary = ({
    continueButton
}: {
    continueButton: ReactNode;
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const startY = useRef(0);
    const panelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (e: MouseEvent | TouchEvent) => {
            if (
                panelRef.current &&
                !panelRef.current.contains(e.target as Node)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener('pointerdown', handleClickOutside);
        return () =>
            document.removeEventListener('pointerdown', handleClickOutside);
    }, [isOpen]);

    return (
        <>
            <div
                ref={panelRef}
                className="fixed bottom-0 left-0 right-0 z-10 touch-none rounded-tl-l rounded-tr-l bg-primary-10 p-xl pt-m text-structure-100"
                onPointerDown={(e) => {
                    startY.current = e.clientY;
                }}
                onPointerUp={(e) => {
                    const delta = startY.current - e.clientY;
                    if (delta > 30) setIsOpen(true);
                    if (delta < -30) setIsOpen(false);
                }}
            >
                {isOpen && (
                    <>
                        <button
                            className="absolute right-m top-m"
                            onClick={() => setIsOpen(false)}
                            data-testid="close-mobile-summary"
                        >
                            <IconTimesDefault fontSize="24px" />
                        </button>

                        <Text className="py-m text-xl font-bold text-structure-100">
                            <FormattedMessage
                                id="_.loan_summary.mobile_summary_heading"
                                defaultMessage="Your loan summary"
                            />
                        </Text>
                    </>
                )}

                <SummaryRow
                    label={
                        <FormattedMessage
                            id="_.loan_summary.monthly_repayment"
                            defaultMessage="Monthly repayment"
                        />
                    }
                    value={
                        <Text className="text-xs font-bold text-structure-100">
                            <FormattedMessage
                                id="_.loan_summary.min_monthly_repayment_mobile"
                                defaultMessage="{amount} /month"
                                values={{
                                    amount: (
                                        <span className="text-l">£--.--</span>
                                    )
                                }}
                            />
                        </Text>
                    }
                />

                <div
                    className="overflow-hidden transition-[max-height] duration-300"
                    style={{ maxHeight: isOpen ? '400px' : '0px' }}
                    data-testid="summary-section"
                >
                    <div className="mt-m flex flex-col gap-s border-t border-secondary-20 pb-xl pt-m">
                        <SummaryRow
                            label={
                                <FormattedMessage
                                    id="_.loan_summary.full_term"
                                    defaultMessage="Full term:"
                                />
                            }
                            value={
                                <FormattedMessage
                                    id="_.loan_summary.default_full_term_value"
                                    defaultMessage="- months"
                                />
                            }
                        />
                        <SummaryRow
                            label={
                                <FormattedMessage
                                    id="_.loan_summary.interest_rate"
                                    defaultMessage="Interest rate /30 days:"
                                />
                            }
                            value="-.--%"
                        />
                        <SummaryRow
                            label={
                                <FormattedMessage
                                    id="_.loan_summary.fees"
                                    defaultMessage="Fees:"
                                />
                            }
                            value="£-"
                        />
                        <SummaryRow
                            label={
                                <FormattedMessage
                                    id="_.loan_summary.total_repayment"
                                    defaultMessage="Total repayment:"
                                />
                            }
                            value="£--.--"
                        />
                    </div>
                </div>

                <div className="pt-l">
                    {isOpen ? (
                        continueButton
                    ) : (
                        <Button
                            className="w-full rounded-full text-primary-10"
                            variant="secondary-alt"
                            onClick={() => setIsOpen(true)}
                        >
                            <FormattedMessage
                                id="_.loan_summary.open_summary_button"
                                defaultMessage="Continue to summary"
                            />
                        </Button>
                    )}
                </div>
            </div>

            {/* Bottom spacer so the content behind the drawer isn't hidden */}
            <div className="h-[160px]" />
        </>
    );
};
